import * as rd from '@devexperts/remote-data-ts'
import { isSuccess, RemoteData } from '@devexperts/remote-data-ts'
import { createSelector } from '@reduxjs/toolkit'

import { ResponseBase } from '@/models/http.ts'
import { Errors } from '@/utils/errors.ts'
import { httpClient } from '../../lib/http/HttpClient'
import {
  FeaturedService,
  GroupIds,
  Service,
  ServiceGroup,
  ServiceInfoPurchaseType,
  ServiceLimitType,
  ServicesInfoResponse,
} from '../../models/service'
import { appSlice } from '../appSlice'
import { resetAllState, setupByUrl } from '../commonActions'
import { RootState } from '../configureStore'
import { createAppSlice } from '../createAppSlice'
import { periodAsUnixTimeSelector } from '../insertPeriodSlice'
import { licenseSlice } from '../licenseSlice'

function getIdsFromQueryString(ids: string | null): GroupIds {
  if (ids === null) {
    return null
  }

  const validIds = ids
    .split(',')
    .map((value) => parseInt(value))
    .filter((number) => !isNaN(number) && number >= 0)

  return validIds.length > 0 ? validIds : null
}

interface ServicesResponse {
  availableQuantity: number
  reservedQuantity: number
  maximumQuantity: number
  hasLimit: boolean
  serviceGroups: ServiceGroup[]
  includedServices: Service[]
  extraFeaturedServices: FeaturedService[]
  extraServices: Service[]
  ticketServices: Service[]
}

type ServicesSliceState = {
  groupIds: GroupIds
  serviceIds: GroupIds
  featuredServices: RemoteData<string, FeaturedService[]>
  services: RemoteData<string, ServicesResponse>
}

const initialState: ServicesSliceState = {
  featuredServices: rd.initial,
  groupIds: null,
  serviceIds: null,
  services: rd.initial,
}

export const servicesSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
    builder.addCase(setupByUrl, (state, action) => {
      const url = new URL(action.payload)
      state.groupIds = getIdsFromQueryString(url.searchParams.get('groupIds'))
      state.serviceIds = getIdsFromQueryString(
        url.searchParams.get('serviceIds')
      )
    })
  },
  initialState: initialState satisfies ServicesSliceState as ServicesSliceState,
  name: 'services',
  reducers: (create) => ({
    fetchFeaturedServices: create.asyncThunk<
      ServicesInfoResponse,
      void,
      { rejectValue: string }
    >(
      async (_, thunkAPI): Promise<ServicesInfoResponse> => {
        const rootState = thunkAPI.getState() as RootState
        const period = periodAsUnixTimeSelector(rootState)
        const country = appSlice.selectors.country(rootState)
        const license = licenseSlice.selectors.licenseCode(rootState)

        if (!period) {
          throw thunkAPI.rejectWithValue('Period not found')
        }

        const res = await httpClient.fetch<ResponseBase<ServicesInfoResponse>>(
          `beaches/${license}/services-info`,
          country,
          {
            body: JSON.stringify({
              featured: true,
              from: period.start,
              loadIndexedData: true,
              serviceType: 1,
              to: period.end,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          }
        )

        if (res.status === 'error') {
          throw thunkAPI.rejectWithValue(res.error)
        }
        return res.data.result
      },
      {
        fulfilled: (state, action) => {
          state.featuredServices = rd.success(
            action.payload.limits.extraServiceLimits
          )
        },
        options: {
          condition(_, thunkApi) {
            const rootState = thunkApi.getState() as RootState
            const featuredServices =
              servicesSlice.selectors.featuredServices(rootState)

            if (rd.isPending(featuredServices)) {
              return false
            }
            return true
          },
        },
        pending: (state) => {
          state.featuredServices = rd.pending
        },
        rejected: (state, action) => {
          state.featuredServices = rd.failure(action.payload || 'Unknown Error')
        },
      }
    ),
    load: create.asyncThunk<
      ServicesInfoResponse,
      { purchaseType: ServiceInfoPurchaseType },
      { rejectValue: string }
    >(
      async ({ purchaseType }, thunkApi): Promise<ServicesInfoResponse> => {
        const rootState = thunkApi.getState() as RootState
        const country = appSlice.selectors.country(rootState)
        const period = periodAsUnixTimeSelector(rootState)
        const isRequestOnlyForTickets =
          purchaseType === ServiceInfoPurchaseType.ALONE
        const license = licenseSlice.selectors.licenseCode(rootState)
        const groupIds = servicesSlice.selectors.groupIds(rootState)
        const serviceIds = servicesSlice.selectors.serviceIds(rootState)

        if (!period) {
          throw thunkApi.rejectWithValue(Errors.PeriodNotFound)
        }

        const res = await httpClient.fetch<ResponseBase<ServicesInfoResponse>>(
          `beaches/${license}/services-info`,
          country,
          {
            body: JSON.stringify({
              from: period.start,
              ...(isRequestOnlyForTickets
                ? { purchaseOnlineAlone: true, serviceType: 3 }
                : { purchaseOnlineWithSpot: true }),
              loadIndexedData: true,
              to: period.end,
              ...(groupIds !== null ? { groupIds } : {}),
              ...(serviceIds !== null ? { serviceIds } : {}),
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          }
        )

        if (res.status === 'error') {
          throw thunkApi.rejectWithValue(res.error)
        }

        return res.data.result
      },
      {
        fulfilled: (state, action) => {
          const limits = action.payload.limits

          const serviceGroups = action.payload.limits.serviceGroupLimits.map(
            (group) => ({
              availableQuantity: group.availableQuantity,
              hasLimit: group.hasLimit,
              maximumQuantity: group.maximumQuantity,
              name: group.name,
              reservedQuantity: group.reservedQuantity,
              serviceGroupId: group.serviceGroupId,
              services: group.serviceLimits,
            })
          )

          const ticketServices = serviceGroups?.flatMap((group) =>
            group.services.map((service) => {
              return {
                ...service,
                globalAvailableQnt: limits.availableQuantity,
                globalMaximumQnt: limits.hasLimit
                  ? limits.maximumQuantity
                  : Infinity,
                globalReservedQnt: limits.reservedQuantity,
                groupAvailableQnt: group.availableQuantity,
                groupMaximumQnt: group.hasLimit
                  ? group.maximumQuantity
                  : Infinity,
                groupReservedQnt: group.reservedQuantity,
                serviceGroupId: group.serviceGroupId,
                serviceGroupName: group.name,
              }
            })
          )

          const extraServicesLimits = action.payload.limits.extraServiceLimits

          const extraServices = extraServicesLimits?.filter(
            (service) =>
              service.minimumLimitType === ServiceLimitType.FLAT &&
              service.minimumQuantity < 1
          )

          const includedServices = extraServicesLimits?.filter(
            (service) =>
              service.minimumLimitType === ServiceLimitType.NUMBER_OF_DAYS ||
              service.minimumLimitType === ServiceLimitType.NUMBER_OF_PIECES ||
              service.minimumQuantity > 0
          )

          state.services = rd.success({
            availableQuantity: limits.availableQuantity,
            extraFeaturedServices:
              action.payload.limits.extraServiceLimits.filter(
                (service) => service.featured
              ),
            extraServices,
            hasLimit: limits.hasLimit,
            includedServices,
            maximumQuantity: limits.maximumQuantity,
            reservedQuantity: limits.reservedQuantity,
            serviceGroups,
            ticketServices,
          })
        },
        options: {
          condition(_, thunkApi) {
            const rootState = thunkApi.getState() as RootState
            const services = servicesSlice.selectors.services(rootState)

            if (rd.isPending(services)) {
              return false
            }
            return true
          },
        },
        pending: (state) => {
          state.services = rd.pending
        },
        rejected: (state, action) => {
          state.services = rd.failure(action.payload || 'Unknown Error')
        },
      }
    ),
  }),
  selectors: {
    data: (state) => (isSuccess(state.services) ? state.services.value : null),
    featuredServices: (state) => state.featuredServices,
    groupIds: (state) => state.groupIds,
    serviceIds: (state) => state.serviceIds,
    services: (state) => state.services,
  },
})

export const filteredServicesSelector = createSelector(
  [servicesSlice.selectors.services, servicesSlice.selectors.groupIds],
  (services, groupIds): ServicesSliceState['services'] => {
    if (groupIds === null) {
      // no filter
      return services
    }
    if (rd.isSuccess(services)) {
      return rd.success({
        ...services.value,
        ticketServices: services.value.ticketServices.filter((ticket) =>
          groupIds.includes(ticket.serviceGroupId)
        ),
      })
    }
    return services
  }
)
export const filteredGroupsSelector = createSelector(
  [servicesSlice.selectors.data, servicesSlice.selectors.groupIds],
  (data, groupIds): ServiceGroup[] => {
    if (data === null) {
      return []
    }

    const serviceGroups = data.serviceGroups.map((group) => ({
      ...group,
      services: group.services.map((service) => {
        return {
          ...service,
          globalAvailableQnt: data.availableQuantity,
          globalMaximumQnt:
            data.maximumQuantity > 0 ? data.maximumQuantity : Infinity,
          globalReservedQnt: data.reservedQuantity,
          groupAvailableQnt: group.availableQuantity,
          groupMaximumQnt:
            group.maximumQuantity > 0 ? group.maximumQuantity : Infinity,
          groupReservedQnt: group.reservedQuantity,
          serviceGroupId: group.serviceGroupId,
        }
      }),
    }))

    if (groupIds === null) {
      // no filter
      return serviceGroups
    }

    return serviceGroups.filter((group) =>
      groupIds.includes(group.serviceGroupId)
    )
  }
)
